<script setup lang="ts">
import type { SuggestionProps } from "@tiptap/suggestion";
import type { ComponentPublicInstance } from "vue";
import type { Command, CommandGroup } from "./commands";
import { isInTable } from "@tiptap/pm/tables";
import PillTag from "ls/components/PillTag.vue";
import MenuEmpty from "ls/components/primitives/menu/MenuEmpty.vue";
import MenuLabel from "ls/components/primitives/menu/MenuLabel.vue";
import { useIsUsingKeyboard } from "ls/composables/useIsUsingKeyboard";
import { computed, ref, watch } from "vue";
import { distinct } from "@/common/lib/array";
import { useDiagramSections } from "../../queries/diagram";
import { useCurrentFileContext } from "../../useCurrentFileContext";
import { useProvideSuggestionContext } from "../suggestions";
import SuggestionList from "../suggestions/SuggestionList.vue";
import { defineCommand, useCommandGroups } from "./commands";
import CommandSectionButton from "./CommandSectionButton.vue";
import { useProvideDocumentCommandContext } from "./context";

const props = defineProps<SuggestionProps>();
const { isDoc } = useCurrentFileContext();
const commands = useCommandGroups(isDoc.value);

const { isBrowseAiButtons, isBrowseSections } = useProvideDocumentCommandContext();

const commandGroups = computed(() => {
  // limit available commands when suggestion is within Table
  if (isInTable(props.editor.state)) {
    return commands.value.tableCommandGroups;
  }

  if (isBrowseAiButtons.value) {
    return commands.value.aiButtonsGroup;
  }

  if (isBrowseSections.value) {
    return [{
      name: "Sections",
      commands: getSectionCommands(),
    }];
  }

  return commands.value.commandGroups;
});

const { data: sections } = useDiagramSections();
function getSectionCommands(): Command[] {
  const commands: Command[] = [];
  if (!sections.value) return [];

  for (const section of sections.value) {
    commands.push(defineCommand({
      title: section.name,
      component: CommandSectionButton,
      props: {
        id: section.id,
        name: section.name,
      },
    }) satisfies Command<typeof CommandSectionButton>,
    );
  }
  return commands;
}

const searchResults = computed(() => {
  const matched = [];

  const groups = commandGroups.value;

  for (const group of groups) {
    const matchedGroup = matchGroup(group, props.query.toLowerCase());
    if (matchedGroup.matchedCommands.length) {
      matched.push(matchedGroup);
    }
  }

  return matched;
});

const matchedCommands = computed(() => {
  return searchResults.value.flatMap(d => d.matchedCommands);
});

type MountedCommandMenuItem = ComponentPublicInstance & { run?: () => void };
const indexToMenuItem = ref<Record<number, MountedCommandMenuItem>>({});

function registerMenuItemCommand(index: number, command: MountedCommandMenuItem) {
  indexToMenuItem.value[index] = command;
}

const {
  selected,
  selectedIndex,
  select,
  onKeyDown,
  onBeforeExit,
  noResults,
} = useProvideSuggestionContext(matchedCommands);

function selectItem(component: MountedCommandMenuItem) {
  component.run?.();
}

selected.on(index => {
  const command = indexToMenuItem.value[index];
  if (command) {
    selectItem(command);
  }
});

function getOngoingIndex(indexGroup: number, indexMenu: number) {
  return searchResults.value.slice(0, indexGroup).reduce((total, group) => {
    return total + group.matchedCommands.length;
  }, 0) + indexMenu;
}

const { isUsingKeyboard } = useIsUsingKeyboard();
const content = ref<HTMLElement>();
watch(selectedIndex, () => {
  if (!content.value) return;
  if (!isUsingKeyboard.value) return;
  const highlighted = content.value.querySelector("[data-highlighted]");
  if (highlighted) {
    highlighted.scrollIntoView({ block: "nearest" });
  }
}, { flush: "post" });

defineExpose({
  onKeyDown,
  onBeforeExit,
});
</script>

<script lang="ts">
function matchesQuery(str: string, query: string) {
  return str.toLowerCase().split(" ").some(part => part.startsWith(query));
}

function matchGroup(group: CommandGroup, query: string) {
  const commands = group.commands;

  const isEmpty = !query.trim().length;
  if (isEmpty) {
    return {
      group,
      matchedCommands: commands.filter(c => !c.hidden),
    };
  }

  if (matchesQuery(group.name, query)) {
    return {
      group,
      matchedCommands: group.commands,
    };
  }
  const matchedByTitle = commands.filter(command => matchesQuery(command.title, query));
  const matchedByAlias = commands.filter(command => command.aliases?.some(alias => alias.startsWith(query)));

  const filteredCommands = [
    ...matchedByTitle,
    ...matchedByAlias,
  ].filter(distinct);

  return {
    group,
    matchedCommands: filteredCommands,
  };
}
</script>

<template>
  <SuggestionList
    v-bind="$props"
  >
    <div ref="content" class="grid max-h-105 min-w-56 grid-cols-[auto_auto_1fr_auto]">
      <MenuEmpty v-if="noResults" />
      <template
        v-for="(menuGroup, indexGroup) in searchResults"
        v-else
        :key="menuGroup.group.name"
      >
        <MenuLabel>
          <div class="flex items-center gap-2">
            {{ menuGroup.group.name }}
            <PillTag v-if="menuGroup.group.alpha">
              alpha
            </PillTag>
          </div>
        </MenuLabel>
        <template
          v-for="(menuItem, indexMenu) in menuGroup.matchedCommands"
          :key="indexGroup + indexMenu"
        >
          <component
            :is="menuItem.component"
            v-if="menuItem.component"
            :ref="(el: any) => registerMenuItemCommand(getOngoingIndex(indexGroup, indexMenu), el)"
            :data-highlighted="selectedIndex === getOngoingIndex(indexGroup, indexMenu) ? '' : undefined"
            :editor="editor"
            :range="range"
            :query="query"
            v-bind="menuItem.props"
            @pointermove="select(getOngoingIndex(indexGroup, indexMenu))"
          />
        </template>
      </template>
    </div>
  </SuggestionList>
</template>
